/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal, trapFocus, removeTrapFocus */

if (!customElements.get('pop-up-v2')) {
  customElements.whenDefined('modal-dialog').then(() => {
    const focusTrappedStyles = ['modal', 'slide-in'];

    class PopUpV2 extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else if (!getCookie(this.cookie) && (this.dataset.showOnMobile === 'true'
          || theme.mediaMatches.md)) {
          if (this.dataset.trigger === 'delay') {
            setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
          } else if (this.dataset.trigger === 'exit' && theme.mediaMatches.md) {
            this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
            document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
          } else if (this.dataset.trigger === 'clipboard') {
            this.copyHandler = this.copyHandler || this.handleCopy.bind(this);
            document.addEventListener('copy', this.copyHandler);
          }
        }
      }

      /**
       * Styles that behave like a dialog and should trap focus.
       * @returns {boolean}
       */
      trapsFocus() {
        return focusTrappedStyles.includes(this.dataset.style);
      }

      /**
       * Triggers when the web component is removed from the DOM
       */
      disconnectedCallback() {
        if (this.mouseLeaveHandler) {
          document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        }

        if (this.copyHandler) {
          document.removeEventListener('copy', this.copyHandler);
        }
      }

      /**
       * Handles 'mouseleave' events on the body.
       * @param {object} evt - Event object.
       */
      handleMouseLeave(evt) {
        if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'copy' events on the body.
       */
      handleCopy() {
        if (!getCookie(this.cookie)) this.open();
      }

      /**
       * Opens the popup. Modal and slide-in trap focus; other styles do not.
       * @param {Element} opener - Element that opened the popup.
       */
      open(opener) {
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }

        this.isClosing = false;
        this.classList.remove('is-closing');
        this.setAttribute('open', '');
        this.openedBy = opener;

        if (this.trapsFocus()) trapFocus(this);

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();
        this.addEventListener('keyup', this.keyupHandler);
      }

      /**
       * Closes the popup after a short outro and remembers the dismissal.
       */
      close() {
        if (!this.hasAttribute('open') || this.isClosing) return;

        this.isClosing = true;
        this.classList.add('is-closing');

        if (this.trapsFocus()) removeTrapFocus(this.openedBy);

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        this.removeEventListener('keyup', this.keyupHandler);

        this.closeTimer = window.setTimeout(() => {
          this.removeAttribute('open');
          this.classList.remove('is-closing');
          this.isClosing = false;
          this.closeTimer = null;

          if (!Shopify.designMode) {
            setCookie(this.cookie, true, this.dataset.dismissDays);
          }
        }, 160);
      }
    }

    customElements.define('pop-up-v2', PopUpV2);
  });
}
